import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  APP_AUTHOR,
  APP_AUTHOR_LINK,
  APP_LINK,
  APP_VERSION,
} from '@/constants/app.constants';
import { trackEvent } from '@/core/utils/analytics';
import { type Theme, useTheme } from '@/hooks/useTheme';
import { useLoanStore } from '@/stores/useLoanStore';
import { exportData, importData } from './data-transfer';

const themeOptions: Array<{ value: Theme; label: string }> = [
  { value: 'light', label: '亮色' },
  { value: 'dark', label: '暗色' },
  { value: 'system', label: '跟随系统' },
];

export function SettingsPage() {
  const clearAll = useLoanStore((s) => s.clearAll);
  const autoSave = useLoanStore((s) => s.autoSave);
  const setAutoSave = useLoanStore((s) => s.setAutoSave);
  const autoUpdate = useLoanStore((s) => s.autoUpdate);
  const setAutoUpdate = useLoanStore((s) => s.setAutoUpdate);
  const { theme, setTheme } = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importResult, setImportResult] = useState('');
  const [updateStatus, setUpdateStatus] = useState('');
  const location = useLocation();

  useEffect(() => {
    if (location.hash === '#donate') {
      document.getElementById('donate')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [location.hash]);

  const handleClearData = () => {
    if (window.confirm('确认清除所有数据？此操作不可撤销。')) {
      clearAll();
      trackEvent('all_data_cleared');
    }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportResult('');
    try {
      const result = await importData(file);
      setImportResult(result);
      trackEvent('app_data_imported');
    } catch (err) {
      setImportResult(err instanceof Error ? err.message : '导入失败');
    }
    // 清空 input 以便重复选择同一文件
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="p-4 lg:p-6 max-w-2xl space-y-4">
      <h2 className="text-lg font-semibold">设置</h2>
      <Card>
        <CardHeader>
          <CardTitle>外观</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            {themeOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  setTheme(opt.value);
                  trackEvent('theme_changed', { theme: opt.value });
                }}
                className={`flex-1 px-3 py-2 rounded-md text-sm border transition-colors ${
                  theme === opt.value
                    ? 'border-primary bg-primary/10 text-primary font-medium'
                    : 'border-border text-muted-foreground hover:bg-muted/10'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>通用</CardTitle>
        </CardHeader>
        <CardContent>
          <label className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">自动保存</p>
              <p className="text-xs text-muted-foreground">
                开启后，每次操作自动保存当前方案和利率表
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={autoSave}
              onClick={() => {
                setAutoSave(!autoSave);
                trackEvent('auto_save_toggled', { enabled: !autoSave });
              }}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                autoSave ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-background shadow-lg ring-0 transition-transform ${
                  autoSave ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </label>
          <label className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">自动更新</p>
              <p className="text-xs text-muted-foreground">
                {autoUpdate
                  ? '检测到新版本时自动更新'
                  : '检测到新版本时弹窗提示'}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={autoUpdate}
              onClick={() => setAutoUpdate(!autoUpdate)}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                autoUpdate ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-background shadow-lg ring-0 transition-transform ${
                  autoUpdate ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </label>
          <div className="flex items-center justify-between pt-2">
            <div>
              <p className="text-sm font-medium">检查更新</p>
              <p className="text-xs text-muted-foreground">
                {updateStatus || '手动检查是否有新版本可用'}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                if (!('serviceWorker' in navigator)) {
                  setUpdateStatus('当前浏览器不支持 Service Worker');
                  return;
                }
                setUpdateStatus('正在检查…');
                try {
                  const reg =
                    await navigator.serviceWorker.getRegistration('/');
                  if (!reg) {
                    setUpdateStatus('未找到 Service Worker 注册');
                    return;
                  }
                  await reg.update();
                  if (reg.waiting) {
                    setUpdateStatus('发现新版本，请在弹窗中确认更新');
                  } else {
                    setUpdateStatus('已是最新版本');
                  }
                } catch {
                  setUpdateStatus('检查失败，请稍后重试');
                }
              }}
            >
              检查更新
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>数据管理</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              导出数据到 JSON
              文件，可在其他设备导入恢复。包含贷款方案（参数+变更记录）和利率表，导入时自动重放计算。
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  exportData();
                  trackEvent('app_data_exported');
                }}
              >
                导出数据
              </Button>
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
              >
                导入数据
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleImport}
                className="hidden"
              />
            </div>
            {importResult && (
              <p className="text-sm text-muted-foreground">{importResult}</p>
            )}
          </div>
          <div className="border-t pt-4 space-y-2">
            <p className="text-sm text-muted-foreground">
              清除所有本地数据，此操作不可撤销。
            </p>
            <Button variant="destructive" onClick={handleClearData}>
              清除所有数据
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>关于</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-2">
          <div id="donate" className="border-t pt-4 mt-4">
            <p className="text-center text-muted-foreground mb-3">
              如果觉得好用，请作者喝杯咖啡 ☕
            </p>
            <div className="flex justify-center">
              <div className="text-center">
                <img
                  src="/donate-wechat.jpg"
                  alt="微信赞赏码"
                  className="w-40 rounded"
                />
                <p className="text-muted-foreground mt-1">微信</p>
              </div>
            </div>
          </div>
          <p>贷款计算器 & 还贷模拟器</p>
          <p className="text-muted-foreground">版本 v{APP_VERSION}</p>
          <p className="text-muted-foreground">
            作者：
            <a
              href={APP_AUTHOR_LINK}
              className="hover:text-primary hover:underline"
              target="_blank"
              rel="noopener"
            >
              {APP_AUTHOR}
            </a>
          </p>
          <p>
            <a
              href={APP_LINK}
              className="text-primary hover:underline"
              target="_blank"
              rel="noopener"
            >
              {APP_LINK}
            </a>
          </p>
          <p>
            <a
              href="https://github.com/DropFan/loan-schedule"
              className="text-primary hover:underline"
              target="_blank"
              rel="noopener"
            >
              GitHub 源码
            </a>
          </p>
          <p className="text-muted-foreground">
            所有数据仅存储在您的设备本地，不会上传到任何服务器
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
